import { useEffect, useMemo, useState } from 'react'
import { CalendarDays, ChevronRight, Radio } from 'lucide-react'
import { type LaunchShow, nextAiringFor } from './launchShows'
import { formatLocalAirtime, remainingParts } from './countdownTime'

type CountdownProps = {
  show: LaunchShow
  compact?: boolean
  onOpenRoom: (show: LaunchShow) => void
}

export default function Countdown({ show, compact = false, onOpenRoom }: CountdownProps) {
  const [now, setNow] = useState(() => new Date())
  const target = useMemo(() => nextAiringFor(show, now), [show, now])
  const remaining = remainingParts(target, now)
  const isPremiere = target.getTime() === new Date(show.premiereIso).getTime()

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1_000)
    return () => window.clearInterval(timer)
  }, [])

  const localTime = formatLocalAirtime(target)

  return (
    <article className={`countdown-card countdown-card--${show.accent} ${compact ? 'countdown-card--compact' : ''}`}>
      <div className="countdown-card__heading">
        <span className="countdown-card__icon"><Radio size={compact ? 15 : 18} /></span>
        <div>
          <span>{isPremiere ? 'SEASON PREMIERE' : 'NEXT EPISODE'}</span>
          <strong>{show.title} · S{show.season}</strong>
        </div>
        <button type="button" onClick={() => onOpenRoom(show)} aria-label={`Open ${show.title} live room`}><ChevronRight size={17} /></button>
      </div>
      <div className="countdown-clock" aria-label={`${remaining.days} days, ${remaining.hours} hours, ${remaining.minutes} minutes and ${remaining.seconds} seconds`}>
        {([
          ['DAYS', remaining.days],
          ['HRS', remaining.hours],
          ['MIN', remaining.minutes],
          ['SEC', remaining.seconds],
        ] as const).map(([label, value]) => <span key={label}><strong>{String(value).padStart(2, '0')}</strong><small>{label}</small></span>)}
      </div>
      <div className="countdown-card__time"><CalendarDays size={13} /><span title="Shown in your device’s local timezone">{localTime}</span><em>{compact ? 'Your local time' : show.nightLabel}</em></div>
    </article>
  )
}
